import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Type, MessageCircle, Server } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
import { Slider } from '@heroui/react';
import styled from '@emotion/styled';
import MCPSettingsView from './MCPSettingsView';
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--theme-paper, #ffffff);
  overflow: hidden;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
  }
`;
const Header = styled.header`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper, #ffffff);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
    border-color: var(--theme-border, #374151);
  }
`;
const Title = styled.h1`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: var(--theme-text, #111827);
  flex: 1;

  .dark & {
    color: var(--theme-text, #f3f4f6);
  }
`;
const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;
const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const CategoryItem = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateX(2px);
  }

  .dark & {
    background: #374151;
    border-color: #4b5563;

    &:hover {
      background: #4b5563;
      border-color: #6b7280;
    }
  }
`;
const CategoryIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 0.5rem;
  color: white;

  &.display {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  }

  &.notifications {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }

  &.privacy {
    background: linear-gradient(135deg, #10b981, #059669);
  }

  &.advanced {
    background: linear-gradient(135deg, #ef4444, #dc2626);
  }
`;
const CategoryInfo = styled.div`
  flex: 1;
`;
const CategoryTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--theme-text, #111827);
  margin-bottom: 0.125rem;

  .dark & {
    color: var(--theme-text, #f3f4f6);
  }
`;
const CategoryDescription = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;

  .dark & {
    color: #9ca3af;
  }
`;
const SettingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const SettingGroup = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;

  .dark & {
    background: #374151;
    border-color: #4b5563;
  }
`;
const SettingTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--theme-text, #111827);
  margin-bottom: 0.75rem;

  .dark & {
    color: var(--theme-text, #f3f4f6);
  }
`;
const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const SliderLabel = styled.span`
  font-size: 0.8125rem;
  color: #6b7280;
  min-width: 2.5rem;

  .dark & {
    color: #9ca3af;
  }
`;
const PreviewText = styled.div`
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: ${(props)=>props.fontSize}px;
  color: #111827;
  text-align: center;
  transition: font-size 0.2s ease;

  .dark & {
    background: #1f2937;
    border-color: #374151;
    color: #f3f4f6;
  }
`;
const GeneralSettingsView = ({ onBack })=>{
    const [currentView, setCurrentView] = useState('main');
    // 글자 크기 상태
    const [fontSizeValue, setFontSizeValue] = useState(()=>{
        const saved = localStorage.getItem('ai-chat-font-size-value');
        return saved ? parseInt(saved) : 14;
    });
    const handleFontSizeChange = (value)=>{
        const size = Array.isArray(value) ? value[0] : value;
        setFontSizeValue(size);
        localStorage.setItem('ai-chat-font-size-value', size.toString());
        window.dispatchEvent(new CustomEvent('ai-chat-font-size-changed', {
            detail: size
        }));
    };
    const handleBackFromSubView = ()=>{
        setCurrentView('main');
    };
    // MCP 설정 뷰 표시
    if (currentView === 'mcp') {
        return /*#__PURE__*/ _jsx(MCPSettingsView, {
            onBack: handleBackFromSubView
        });
    }
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        iconOnly: true,
                        icon: /*#__PURE__*/ _jsx(ArrowLeft, {
                            className: "w-4 h-4"
                        }),
                        variant: "ghost",
                        size: "sm",
                        onClick: currentView === 'main' ? onBack : handleBackFromSubView,
                        label: "뒤로"
                    }),
                    /*#__PURE__*/ _jsxs(Title, {
                        children: [
                            currentView === 'main' && 'AI 채팅 설정',
                            currentView === 'display' && '화면 설정',
                            currentView === 'chat' && '채팅 설정'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Content, {
                children: [
                    currentView === 'main' && /*#__PURE__*/ _jsxs(CategoryList, {
                        children: [
                            /*#__PURE__*/ _jsxs(CategoryItem, {
                                onClick: ()=>setCurrentView('display'),
                                whileHover: {
                                    scale: 1.01
                                },
                                whileTap: {
                                    scale: 0.99
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(CategoryIcon, {
                                        className: "display",
                                        children: /*#__PURE__*/ _jsx(Type, {
                                            size: 20
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(CategoryInfo, {
                                        children: [
                                            /*#__PURE__*/ _jsx(CategoryTitle, {
                                                children: "화면 설정"
                                            }),
                                            /*#__PURE__*/ _jsx(CategoryDescription, {
                                                children: "글자 크기, 테마, 레이아웃 설정"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(CategoryItem, {
                                onClick: ()=>setCurrentView('chat'),
                                whileHover: {
                                    scale: 1.01
                                },
                                whileTap: {
                                    scale: 0.99
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(CategoryIcon, {
                                        className: "notifications",
                                        children: /*#__PURE__*/ _jsx(MessageCircle, {
                                            size: 20
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(CategoryInfo, {
                                        children: [
                                            /*#__PURE__*/ _jsx(CategoryTitle, {
                                                children: "채팅 설정"
                                            }),
                                            /*#__PURE__*/ _jsx(CategoryDescription, {
                                                children: "AI 응답 및 대화 설정"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(CategoryItem, {
                                onClick: ()=>setCurrentView('mcp'),
                                whileHover: {
                                    scale: 1.01
                                },
                                whileTap: {
                                    scale: 0.99
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(CategoryIcon, {
                                        className: "advanced",
                                        children: /*#__PURE__*/ _jsx(Server, {
                                            size: 20
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(CategoryInfo, {
                                        children: [
                                            /*#__PURE__*/ _jsx(CategoryTitle, {
                                                children: "MCP 서버"
                                            }),
                                            /*#__PURE__*/ _jsx(CategoryDescription, {
                                                children: "Model Context Protocol 서버 관리"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    currentView === 'display' && /*#__PURE__*/ _jsx(SettingSection, {
                        children: /*#__PURE__*/ _jsxs(SettingGroup, {
                            children: [
                                /*#__PURE__*/ _jsx(SettingTitle, {
                                    children: "글자 크기"
                                }),
                                /*#__PURE__*/ _jsxs(SliderContainer, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(SliderRow, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SliderLabel, {
                                                    children: "작게"
                                                }),
                                                /*#__PURE__*/ _jsx(Slider, {
                                                    size: "md",
                                                    step: 1,
                                                    minValue: 10,
                                                    maxValue: 20,
                                                    value: fontSizeValue,
                                                    onChange: handleFontSizeChange,
                                                    "aria-label": "글자 크기 조절",
                                                    color: "primary",
                                                    showTooltip: true,
                                                    showSteps: false,
                                                    classNames: {
                                                        base: "max-w-md",
                                                        track: "bg-default-100 border-s-primary-100",
                                                        filler: "bg-gradient-to-r from-blue-500 to-blue-600"
                                                    },
                                                    tooltipProps: {
                                                        content: `${fontSizeValue}px`,
                                                        placement: 'bottom',
                                                        classNames: {
                                                            base: "py-1 px-2 text-xs rounded-md",
                                                            content: "text-small font-bold"
                                                        }
                                                    }
                                                }),
                                                /*#__PURE__*/ _jsx(SliderLabel, {
                                                    children: "크게"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(PreviewText, {
                                            fontSize: fontSizeValue,
                                            children: "가나다라 ABC 123"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            style: {
                                                fontSize: '0.75rem',
                                                color: '#6b7280',
                                                textAlign: 'center'
                                            },
                                            children: [
                                                "현재 크기: ",
                                                /*#__PURE__*/ _jsxs("span", {
                                                    style: {
                                                        fontWeight: '600',
                                                        color: '#3b82f6'
                                                    },
                                                    children: [
                                                        fontSizeValue,
                                                        "px"
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    currentView === 'chat' && /*#__PURE__*/ _jsxs(SettingSection, {
                        children: [
                            /*#__PURE__*/ _jsxs(SettingGroup, {
                                children: [
                                    /*#__PURE__*/ _jsx(SettingTitle, {
                                        children: "AI 모델 설정"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            fontSize: '0.8125rem',
                                            color: '#6b7280',
                                            marginBottom: '1rem'
                                        },
                                        children: "사용할 AI 모델과 응답 방식을 설정합니다."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SettingGroup, {
                                children: [
                                    /*#__PURE__*/ _jsx(SettingTitle, {
                                        children: "대화 기록"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            fontSize: '0.8125rem',
                                            color: '#6b7280'
                                        },
                                        children: "대화 기록 자동 저장 및 관리 설정"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SettingGroup, {
                                children: [
                                    /*#__PURE__*/ _jsx(SettingTitle, {
                                        children: "응답 스타일"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            fontSize: '0.8125rem',
                                            color: '#6b7280'
                                        },
                                        children: "AI의 응답 길이, 상세도, 톤 설정"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default GeneralSettingsView;
